/**
 * Строка «X печатает…» НАД композером (Антон, 2026-09-16 21:27 UTC).
 *
 * Формулировки (как в Element, но по-русски и по-английски):
 *   1 человек : «<A> печатает…» / “<A> is typing…”
 *   2         : «<A> и <B> печатают…» / “<A> and <B> are typing…”
 *   3+        : «<A>, <B> и ещё N печатают…» / “<A>, <B> and N more are typing…”
 * Себя не показываем — это отфильтровано в useTypingNames.
 *
 * Три точки — лёгкая CSS-анимация без зависимостей: псевдо-элемент steps().
 * Никаких библиотек анимации, постановка прямо просит «без тяжёлых
 * зависимостей».
 *
 * Тонкая строка, а не плашка: индикатор живёт пару секунд и не должен
 * дергать высоту композера — резервируем строку всегда? НЕТ: когда никто
 * не печатает, компонент возвращает null и место не занимает. Элемент так
 * же ведёт себя — лента не прыгает, потому что строка появляется НАД
 * композером в потоке, сдвигая только композер на ~20px. Это заметнее,
 * чем ничего, но постановка — «показывать строку над композером».
 */
import { useTF } from "../i18n";
import type { MatrixClient, Room } from "matrix-js-sdk";

import { useTypingNames } from "../matrix/typing";
import { font, space } from "./theme";
import { palette } from "./styles";

/** Ключевые кадры многоточия — вставляем через <style>, как composerCss. */
const typingCss = `
.alg-typing-dots::after {
  content: "";
  animation: alg-typing-dots 1.2s steps(4, end) infinite;
}
@keyframes alg-typing-dots {
  0%   { content: ""; }
  25%  { content: "."; }
  50%  { content: ".."; }
  75%  { content: "..."; }
  100% { content: ""; }
}
`;

export function TypingIndicator({
  client,
  room,
}: {
  client: MatrixClient | null;
  room: Room | null;
}) {
  const tfmt = useTF();
  const names = useTypingNames(client, room);

  if (names.length === 0) return null;

  // Многоточие — анимированное, поэтому в переводах оно НЕ дублируется:
  // ключи заканчиваются на «…» визуально через .alg-typing-dots.
  let text: string;
  if (names.length === 1) {
    text = tfmt("typing.one", { name: names[0] });
  } else if (names.length === 2) {
    text = tfmt("typing.two", { a: names[0], b: names[1] });
  } else {
    text = tfmt("typing.more", {
      a: names[0],
      b: names[1],
      count: names.length - 2,
    });
  }

  return (
    <div
      style={{
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        gap: space.xs,
        padding: `0 16px ${space.xs}px`,
        fontSize: font.tiny,
        color: palette.textDim,
        minHeight: 0,
      }}
      data-testid="typing-indicator"
      aria-live="polite"
      role="status"
    >
      <style>{typingCss}</style>
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {text}
        <span className="alg-typing-dots" aria-hidden="true" />
      </span>
    </div>
  );
}
